import React from 'react';
import {
  ActivityIndicator,
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { colors, controlHeights, radii, spacing } from '../../theme';
import Icon, { IconName } from './Icon';
import Text from './Text';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'whatsapp';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = Omit<PressableProps, 'style'> & {
  label: string;
  /**
   * `primary` - filled forest green ("Get Started", "Book Now").
   * `secondary` - pale sage fill ("Next").
   * `outline` - hairline outline on the canvas.
   * `ghost` - bare text link ("Log In", "Skip for Now").
   * `danger` - the irreversible one ("Cancel booking", "Remove photo").
   *
   * `whatsapp` - WhatsApp's own green, for the one control that leaves for
   * WhatsApp. A variant rather than a `style` override because `style` is
   * applied last and would win over the pressed fill, leaving a button that
   * does not react to being pressed; and because two screens draw this and
   * neither should be inventing the shade.
   *
   * `danger` is a pale fill with red ink rather than a solid red button, and
   * that is deliberate: solid red is the loudest thing on any screen, which
   * puts the design's whole weight behind the choice nobody should make by
   * accident. Pale, it is unmistakably the destructive control and
   * unmistakably not the default.
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretches to the container width. Full-width is the design default. */
  fullWidth?: boolean;
  leadingIcon?: IconName;
  trailingIcon?: IconName;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
};

const fills: Record<ButtonVariant, ViewStyle> = {
  primary: { backgroundColor: colors.primary },
  secondary: { backgroundColor: colors.surfaceMuted },
  outline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.borderStrong,
  },
  ghost: { backgroundColor: 'transparent' },
  danger: {
    backgroundColor: colors.dangerSurface,
    borderWidth: 1,
    borderColor: colors.danger,
  },
  whatsapp: { backgroundColor: colors.whatsapp },
};

const pressedFills: Record<ButtonVariant, ViewStyle> = {
  primary: { backgroundColor: colors.primaryPressed },
  secondary: { backgroundColor: colors.border },
  outline: { backgroundColor: colors.surfaceMuted },
  ghost: { opacity: 0.6 },
  danger: { opacity: 0.7 },
  whatsapp: { backgroundColor: colors.whatsappPressed },
};

/**
 * What a button that cannot be pressed is drawn as.
 *
 * A real fill and a real ink, rather than the `opacity: 0.45` this used to
 * put over the whole control. Fading a white label on a forest fill lands it
 * near 2:1 against the page - the "Confirm" on the slot picker was the
 * reported case, legible as a shape but not as a word.
 *
 * `surfaceMuted` with `inkDisabled` keeps the button's footprint exactly
 * where it was, states plainly that it is not available, and stays readable.
 * The bordered variants keep a border so the shape does not dissolve.
 */
const inertFills: Record<ButtonVariant, ViewStyle> = {
  primary: { backgroundColor: colors.surfaceMuted },
  secondary: { backgroundColor: colors.surfaceMuted },
  outline: {
    backgroundColor: colors.surfaceMuted,
    borderColor: colors.border,
  },
  /* Ghost has no fill to mute - dimming the ink is the whole of its
     disabled state, and a fill here would invent a button that is not there. */
  ghost: {},
  danger: {
    backgroundColor: colors.surfaceMuted,
    borderColor: colors.border,
  },
  whatsapp: { backgroundColor: colors.surfaceMuted },
};

const contentColors: Record<ButtonVariant, string> = {
  primary: colors.onPrimary,
  secondary: colors.ink,
  outline: colors.ink,
  ghost: colors.ink,
  danger: colors.danger,
  whatsapp: colors.onWhatsapp,
};

function Button({
  label,
  variant = 'primary',
  size = 'lg',
  fullWidth = true,
  leadingIcon,
  trailingIcon,
  loading = false,
  disabled,
  style,
  ...rest
}: ButtonProps) {
  const isInert = disabled || loading;
  /* Disabled swaps the ink too, not just the fill. `primary`'s white label on
     the pale `inertFills` fill would be very nearly invisible otherwise -
     which is the failure `inertFills` exists to end, not to relocate.

     `loading` keeps the live ink: the spinner is drawn in `tint`, and a
     button that is working is not a button that is unavailable. */
  const tint =
    disabled && !loading ? colors.inkDisabled : contentColors[variant];

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: !!isInert, busy: loading }}
      disabled={isInert}
      {...rest}
      style={({ pressed }) => [
        styles.base,
        { height: controlHeights[size] },
        variant === 'ghost' ? styles.ghostPadding : styles.padded,
        fills[variant],
        fullWidth ? styles.fullWidth : styles.hugContent,
        pressed && !isInert ? pressedFills[variant] : null,
        isInert ? inertFills[variant] : null,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator color={tint} size="small" />
      ) : (
        <View style={styles.content}>
          {leadingIcon ? <Icon name={leadingIcon} size={18} color={tint} /> : null}
          <Text variant="label" color={tint}>
            {label}
          </Text>
          {trailingIcon ? <Icon name={trailingIcon} size={18} color={tint} /> : null}
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  padded: {
    paddingHorizontal: spacing.xl,
  },
  ghostPadding: {
    paddingHorizontal: spacing.sm,
  },
  fullWidth: {
    alignSelf: 'stretch',
  },
  hugContent: {
    alignSelf: 'flex-start',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
});

export default Button;
